import { Flex, HoleInfo, ScoreBadge, Typography } from '@slope/uikit';
import React from 'react';
import styled, { keyframes } from 'styled-components';
import { ScoreCard } from '@slope/uikit';
import { GreenInfo } from '../GreenInfo';
import { useStore } from '@slope/store';
export type Orientation = 'horizontal' | 'vertical';

/* ---------- Helpers ---------- */
const eventColor = (t: HighlightEventType) => {
  switch (t) {
    case 'EAGLE':
      return '#1e88e5';
    case 'BIRDIE':
      return '#e53935';
    case 'PAR':
      return '#546e7a';
    case 'BOGEY':
      return '#fb8c00';
    case 'DOUBLE_OR_WORSE':
      return '#8e24aa';
    default:
      return '#333';
  }
};

const fmtRelative = (d: Date) => {
  const s = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (s < 60) return `hace ${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `hace ${m}m`;
  const h = Math.floor(m / 60);
  return `hace ${h}h`;
};

/* ---------- Styled ---------- */
const Wrapper = styled.section`
  display: grid;
  grid-template-areas:
    'header header header header header header'
    'hole-info green-info score-card score-card score-card score-card'
    'summary-chart summary-chart summary-chart stat-chart stat-chart stat-chart'
    'summary-chart summary-chart summary-chart birdies pairs eagles';
  gap: 16px 16px;
  max-height: 100%;
  box-sizing: border-box;
  color: #1b1b1b;

  @media (max-width: 1100px) {
    grid-template-columns: 320px 1fr;
    grid-template-areas:
      'header header'
      'score-card score-card'
      'hole-info green-info'
      'eagles birdies'
      'pairs pairs'
      'stat-chart summary-chart';
  }

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
    grid-template-areas:
      'header'
      'score-card'
      'hole-info'
      'green-info'
      'eagles'
      'birdies'
      'pairs'
      'stat-chart'
      'summary-chart';
  }
`;

type GridAreaProps = {
  area: string;
  overflow?: React.CSSProperties['overflow'];
};

const GridArea = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'area',
})<GridAreaProps>`
  display: grid;
  grid-area: ${({ area }) => area};
  overflow: ${({ overflow = 'visible' }) => overflow};
`;

const Header = styled.header<{ type: HighlightEventType }>`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: start;
  gap: 12px;

  .sub {
    display: flex;
    gap: 10px;
    align-items: center;
    margin-top: 6px;
    font-size: 18px;
    color: #2b2b2b;
  }
  .pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 34px;
    height: 28px;
    padding: 0 8px;
    border-radius: 999px;
    font-weight: 800;
    color: #fff;
    background: ${({ type }) => eventColor(type)};
  }
  .ago {
    color: #6b6f76;
    font-size: 14px;
    margin-left: 6px;
  }
`;

const Bucket = styled(Flex)`
  border: 1px solid rgba(146, 146, 146, 0.1);
  border-radius: 12px;
  padding: 16px;
  gap: 8px;
  flex-grow: 1;
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.8);
  border-radius: 16px;
`;

/* playful subtle pop on event title */
const pop = keyframes`
  from { transform: translateY(8px); opacity: .0 }
  to   { transform: translateY(0);    opacity: 1 }
`;

const AnimatedTitle = styled(Typography)`
  animation: ${pop} 0.25s ease-out both;
`;

export interface BarLabelOptions {
  orientation: Orientation;
  /** px padding from the bar edge */
  pad?: number;
  /** if bar < threshold (px), draw label outside for readability */
  smallThreshold?: number;
  colorInside?: string;
  colorOutside?: string;
  fontSize?: React.CSSProperties['fontSize'];
  fontWeight?: React.CSSProperties['fontWeight'];
}

/* ---------- Types ---------- */
export type HighlightEventType = 'EAGLE' | 'BIRDIE' | 'PAR' | 'BOGEY' | 'DOUBLE_OR_WORSE';

export type Wind = { speedMs: number; dirDeg: number };

export type HoleStatic = {
  number: number;
  par: number;
  strokeIndex: number;
  orientationDeg?: number; // optional, to render the compass
  pinPosition?: string; // "5F 10D"
  historicalAvg?: number; // 4.35
};

export type PlayerInfo = {
  name: string; // "Harrell Sutton"
  rank?: string; // "4"
  score?: number | null; // -2 (the pink bubble)
  total?: number | null; // -7 (the red bubble)
  scoreStrip?: (number | string)[]; // [4,4,5,4, ...]
};

export type Tally = { label: string; value: number | null };

export type HighlightProps = {
  title: string; // optional title above
  eventType: HighlightEventType;
  hole: HoleStatic;
  strip: { hole: number; strokes: number | null; par: number }[];
  player: PlayerInfo;
  timestamp: string | number | Date; // event timestamp
  wind?: Wind;
  eagles?: string[]; // names for bucket
  birdies?: string[];
  pairs?: string[];
  talliesLeft?: Tally[]; // small bar summary (avg / today etc)
  talliesRight?: Tally[]; // bogeys / double-peor etc
};

/* ---------- Component ---------- */
export const Highlight: React.FC<HighlightProps> = ({
  title,
  eventType,
  hole,
  player,
  timestamp,
  strip = [],
  //wind,
}) => {
  const ts = new Date(timestamp);
  const rel = fmtRelative(ts);

  const holeObject = useStore(({ hole }) => hole);

  const { holes, scores } = React.useMemo(
    () =>
      strip
        .sort((a, b) => a.hole - b.hole)
        .reduce(
          (acc, { strokes, par }) => {
            return {
              holes: acc.holes + 1,
              scores: [...acc.scores, { strokes: strokes ?? null, par: par ?? null, stableford: null }],
            };
          },
          { holes: 0, scores: [] } as {
            holes: number;
            scores: { strokes: number | null; par: number | null; stableford: number | null }[];
          },
        ),
    [strip],
  );

  return (
    <Wrapper>
      <GridArea area="header">
        <Header type={eventType}>
          <Bucket>
            <AnimatedTitle fontSize="2.5rem" fontWeight="400">
              {title}
              <span className="ago"> {rel}</span>
            </AnimatedTitle>

            <div className="sub">
             {/*  <Typography fontSize="1.8rem" fontWeight="400" color={'#6b6f76'}>
                {player.rank}
              </Typography> */}
              <Typography fontSize="1.8rem" fontWeight="500">
                {player.name}
              </Typography>
              {typeof player.score === 'number' && (
                <ScoreBadge size="2.5rem" value={player.score} variant="outlined" sign/>
              )}
              {typeof player.total === 'number' && (
                <ScoreBadge size="2.5rem" value={player.total} sign/>
              )}
            </div>
          </Bucket>
        </Header>
      </GridArea>
      <GridArea area="score-card">
        <Bucket justifyContent="center">
          <ScoreCard holes={holes} scores={scores} showTotals />
        </Bucket>
      </GridArea>
      <GridArea area="hole-info">
        <Bucket justifyContent="center" alignItems="center">
          <HoleInfo {...hole} />
        </Bucket>
      </GridArea>
      <GridArea area="green-info">
        <Bucket justifyContent="center" alignItems="center">
          {holeObject && <GreenInfo hole={holeObject} />} 
          {/* {hole.number > 3 && holeObject ? (
            <GreenInfo hole={holeObject} />
          ) : (
            <video loop muted autoPlay>
              <source
                src="https://www.flickr.com/photos/197027546@N07/54791190631/play/orig/c5f21f34b8/"
                type="video/mp4"
              />
            </video>
          )} */}
        </Bucket>
      </GridArea> 

      {/* <GridArea area="eagles">
        <Bucket>
          <List>
            <Typography fontSize={'1.2rem'}>Eagles</Typography>
            <ul>
              {eagles.map((n, i) => (
                <li key={i}>
                  <Typography color="#aaaaaa">{n}</Typography>
                </li>
              ))}
            </ul>
          </List>
        </Bucket>
      </GridArea>
      <GridArea area="birdies">
        <Bucket>
          <List>
            <Typography fontSize={'1.2rem'}>Birdies</Typography>
            <ul>
              {birdies.map((n, i) => (
                <li key={i}>
                  <Typography color="#aaaaaa">{n}</Typography>
                </li>
              ))}
            </ul>
          </List>
        </Bucket>
      </GridArea> */}
      {/* <GridArea area="pairs">
        <Bucket>
          <List>
            <Typography fontSize={'1.2rem'}>Pairs</Typography>
            <ul>
              {pairs.map((n, i) => (
                <li key={i}>
                  <Typography color="#aaaaaa">{n}</Typography>
                </li>
              ))}
            </ul>
          </List>
        </Bucket>
      </GridArea> */}
     {/*  <GridArea area="stat-chart" overflow="hidden">
        <Bucket>
          <ChartBars orientation="vertical" data={talliesLeft} hideXTicks color="#185232" />
        </Bucket>
      </GridArea> */}
      {/* <GridArea area="summary-chart" overflow="hidden">
        <Bucket>
          <ChartBars data={talliesRight} hideXTicks getColor={barStrokeColor} />
        </Bucket>
      </GridArea> */}
    </Wrapper>
  );
};
